<template>
	<PrimeVueDataTable
		ref="itemTable"
		:key="datatableKey"
		v-model:selection="selectedItems"
		v-model:filters="filters"
		:expanded-rows="expandedRows"
		:global-filter-fields
		:data-key
		:value
		:size
		removable-sort
		row-hover
		:lazy="!!total"
		:rows
		:rows-per-page-options="[10, 25, 50]"
		:row-class="rowClass"
		:total-records="total"
		:auto-layout="false"
		:show-headers
		:paginator
		:table-class
		:class="containerClass"
		:loading
		:filter-display="computedFilterDisplay"
		:sort-field="serverOptions?.sortBy ?? sortBy"
		:sort-order="serverOptions?.sortOrder ?? sortOrder"
		:group-rows-by="groupRowsBy"
		row-group-mode="subheader"
		data-testid="datatable"
		:virtual-scroller-options="useVirtualScrolling ? { itemSize: 44 } : undefined"
		:paginator-template="props.paginatorTemplate"
		@update:expanded-rows="onExpandedRowsChange"
		@page="(params) => emit('update:serverOptions', formatParams(params))"
		@sort="(params) => emit('update:serverOptions', formatParams(params))"
		@row-click="(row) => clickedRow(row)"
		@row-select="(row) => selectRow(row)"
		@row-unselect="emit('update:itemsSelected', selections)"
		@row-select-all="selectAll()"
		@row-unselect-all="selectAll()"
		@row-expand="handleKitColumnWidths">
		<!-- Column Controls -->
		<Column
			v-if="expandable && (kits.length > 0 || $slots.expansion)"
			expander
			:style="controlColumnStyle">
			<template #header>
				<expand-rows
					v-if="expandable && kits.length > 0"
					:expanded="Object.keys(expandedRows).length == kits.length"
					@expand-all="expandAll()"
					@close-all="updateExpandedRows({})" />
			</template>
		</Column>
		<Column v-if="selectable" selection-mode="multiple" :style="controlColumnStyle"></Column>

		<!-- Data columns -->
		<Column
			v-for="header in headers"
			:key="header.value"
			:field="header.field ? header.field : header.value"
			:header="header.text"
			v-bind="header.props"
			:style="columnStyle(header.props?.width)">
			<!-- Auto-apply status badges -->
			<template v-if="header.value == 'stock_status' && statusKey" #body="slotProps">
				<app-badge
					class="gap-2"
					size="small"
					:theme="getStatusClass(statusParam(slotProps.data, 'status', statusKey))">
					{{ statusParam(slotProps.data, 'status', statusKey) }}
				</app-badge>
			</template>

			<!-- Row body slot (e.g. row-item_number) -->
			<template v-else-if="$slots[`row-${header.value}`]" #body="slotProps">
				<slot :name="`row-${header.value}`" :row="slotProps.data"></slot>
			</template>

			<!-- Row filter slot (e.g. filter-item_number) -->
			<template
				v-if="$slots[`filter-${header.value}`] && globalFilterFields.includes(header.value)"
				#filter="{ filterModel, filterCallback }">
				<slot
					:name="`filter-${header.value}`"
					:model="filterModel"
					:callback="filterCallback"></slot>
			</template>
		</Column>

		<!-- Group Header Slot -->
		<template v-if="$slots.groupheader" #groupheader="slotProps">
			<slot name="groupheader" :data="slotProps.data" :index="slotProps.index"></slot>
		</template>

		<!-- Expanded row content (kit components) -->
		<template #expansion="slotProps">
			<slot name="expansion" :data="slotProps.data" :index="slotProps.index">
				<div>
					<PrimeVueDataTable
						v-model:selection="selectedComponents"
						:value="slotProps.data.kit_components"
						:data-key
						:show-headers="false"
						:paginator="false"
						:row-class="rowClass"
						class="kit-table"
						:auto-layout="false"
						data-testid="expanded-datatable"
						@row-click="(row) => clickedRow(row)"
						@row-select="(row) => selectRow(row)">
						<!-- Column Controls -->
						<Column expander :style="controlColumnStyle" />
						<Column
							v-if="selectable"
							selection-mode="multiple"
							:style="controlColumnStyle"></Column>

						<Column
							v-for="(header, index) in headers"
							:key="header.value"
							:field="header.value"
							:header="header.text"
							v-bind="header.props"
							:style="kitColumnStyle(index, header.props?.width)">
							<!-- Auto-apply status badges -->
							<template
								v-if="header.value == 'stock_status' && statusKey"
								#body="kitProps">
								<app-badge
									class="gap-2"
									size="small"
									:theme="
										getStatusClass(
											statusParam(kitProps.data, 'status', statusKey)
										)
									">
									{{ statusParam(kitProps.data, 'status', statusKey) }}
								</app-badge>
							</template>

							<template v-else-if="$slots[`row-${header.value}`]" #body="kitProps">
								<slot :name="`row-${header.value}`" :row="kitProps.data"></slot>
							</template>
						</Column>
					</PrimeVueDataTable>
				</div>
			</slot>
		</template>
	</PrimeVueDataTable>
</template>

<script lang="ts" setup>
	const useVirtualScrolling = computed(() => {
		return window.innerWidth >= 1024
	})

	import PrimeVueDataTable from 'primevue/datatable'
	import Column from 'primevue/column'
	import { computed, nextTick, onMounted, ref, watch } from 'vue'
	import { getStatusClass, useSelectedItemsHandler } from '@/composables/datatable'
	import type { WorkspaceArea } from '@/data/types/area'
	import type { Header, Params, Row } from '@/data/types/datatable'
	import { statusParam } from '@/composables/contracts'
	import { useKits } from '@/composables/kits'
	import ExpandRows from '@/components/partials/tables/ExpandRows.vue'

	interface Props {
		serverOptions?: Params
		expandedRows?: any[] | Record<string, any>
		itemsSelected?: Row[]
		filterOptions?: object
		globalFilterFields?: string[]
		dataKey?: string
		statusKey?: WorkspaceArea
		headers: Header[]
		rows?: number
		total?: number
		value: object[]
		highlightSelections?: boolean
		highlightClicked?: boolean
		highlightSkipped?: boolean
		// eslint-disable-next-line no-unused-vars
		bodyRowClassName?: (row: object) => string
		selectable?: boolean
		expandable?: boolean
		clickable?: boolean
		showHeaders?: boolean
		groupRowsBy?: string
		paginator?: boolean
		compact?: boolean
		size?: 'small' | 'large'
		tableClass?: string | null
		containerClass?: string | null
		loading?: boolean
		resetSelections?: boolean
		sortBy?: string
		sortOrder?: number
		filterDisplay?: 'row' | 'menu'
		paginatorTemplate?: string
	}

	const props = withDefaults(defineProps<Props>(), {
		serverOptions: undefined,
		expandedRows: undefined,
		itemsSelected: () => [],
		filterOptions: undefined,
		globalFilterFields: undefined,
		dataKey: 'id',
		statusKey: undefined,
		rows: 10,
		total: undefined,
		value: () => [],
		highlightSelections: false,
		highlightClicked: false,
		highlightSkipped: true,
		bodyRowClassName: undefined,
		selectable: false,
		expandable: false,
		clickable: false,
		showHeaders: true,
		groupRowsBy: '',
		paginator: true,
		compact: false,
		size: 'small',
		tableClass: null,
		containerClass: null,
		loading: false,
		resetSelections: false,
		sortBy: undefined,
		sortOrder: 1,
		filterDisplay: undefined,
		paginatorTemplate: 'PrevPageLink PageLinks NextPageLink RowsPerPageDropdown' // Default
	})

	const filters = ref()

	// Data
	const datatableKey = ref(0)
	const itemTable = ref()
	const localExpandedRows = ref(props.expandedRows || {})
	const selectedItems = ref(props.itemsSelected)
	const selectedComponents = ref([])
	const clicked = ref<Row | null | undefined>()
	const { kits } = useKits(computed(() => props.value))
	const colWidths = ref()

	const expandedRows = computed(() => props.expandedRows ?? localExpandedRows.value)

	const updateExpandedRows = (val: any) => {
		if (JSON.stringify(val) === JSON.stringify(expandedRows.value)) return
		localExpandedRows.value = val
		emit('update:expandedRows', val)
	}

	const onExpandedRowsChange = (val: any) => {
		updateExpandedRows(val)
	}

	watch(
		() => props.expandedRows,
		(newVal) => {
			if (newVal && JSON.stringify(newVal) !== JSON.stringify(localExpandedRows.value)) {
				localExpandedRows.value = newVal
			}
		},
		{ deep: true }
	)

	const selections = computed(() => {
		return [...selectedItems.value, ...selectedComponents.value].filter(
			(item) => item.selectable ?? true
		)
	})

	const controlColumnStyle = computed(() => {
		if (props.size == 'large') {
			return {
				width: '4.19rem !important',
				minWidth: '4.19rem !important'
			}
		}

		return {
			width: '3rem !important',
			minWidth: '3rem !important'
		}
	})

	const computedFilterDisplay = computed(() => {
		if (props.filterDisplay) {
			return props.filterDisplay
		}
		return props.filterOptions &&
			props.globalFilterFields &&
			props.globalFilterFields.length > 0
			? 'row'
			: undefined
	})

	const rowClass = (data) => {
		return [
			{
				'!text-[12px] border-gray-300': true,
				'not-expandable': !data.kit_components,
				'component-row': data.type == 'Component',
				'item-row': data.type != 'Component',
				'bg-[#ff77a8] text-white': props.highlightSelections && isSelected(data),
				'bg-mabey-950 text-white': props.highlightClicked && clicked.value?.id == data.id,
				'bg-red-400 text-white':
					props.highlightSkipped &&
					statusParam(data, 'status', props.statusKey) == 'Skipped',
				'cursor-pointer': props.clickable,
				'compact-row': props.compact
			},
			props.bodyRowClassName ? props.bodyRowClassName(data) : undefined
		]
	}

	// Functions

	function formatParams(params: Params) {
		const rowsPerPage = params.rows ?? props.rows
		const page =
			params.page !== undefined
				? params.page + 1
				: params.first !== undefined && rowsPerPage
					? params.first / rowsPerPage + 1
					: 1
		return {
			page,
			pageCount: params.pageCount,
			rowsPerPage,
			sortBy: params.sortField,
			sortType: params.sortOrder == 1 ? 'asc' : 'desc',
			sortField: params.sortField,
			sortOrder: params.sortOrder
		}
	}

	function clickedRow(row: Row) {
		const trigger = row.originalEvent.target

		// Prevent emit if expand icon or checkbox is clicked
		if (
			trigger.closest('path') ||
			trigger.closest('svg') ||
			trigger.closest('.p-selection-column')
		) {
			return
		}

		// Prevent emit if item is a kit
		// Expand instead
		if (row.data.type === 'Kit') {
			const newExpandedRows = { ...expandedRows.value }
			newExpandedRows[row.data.id] = newExpandedRows[row.data.id] ? undefined : true
			updateExpandedRows(newExpandedRows)
			return
		}

		// Handle row selection on click
		if (props.clickable && props.selectable) {
			const rowData = row.data
			const dataKey = props.dataKey
			const index = selectedItems.value.findIndex(
				(item) => item[dataKey] === rowData[dataKey]
			)

			if (index > -1) {
				selectedItems.value = selectedItems.value.filter(
					(item) => item[dataKey] !== rowData[dataKey]
				)
			} else {
				selectedItems.value = [...selectedItems.value, rowData]
			}
			emit('update:itemsSelected', selectedItems.value)
		}

		clicked.value = row.data
		emit('rowClick', row.data)
	}

	function expandAll() {
		if (kits.value.length > 0) {
			const newExpandedRows = kits.value.reduce((rows, kit) => {
				rows[kit.id] = true
				return rows
			}, {})
			updateExpandedRows(newExpandedRows)
		}

		handleKitColumnWidths()
	}

	function selectRow(row: Row) {
		emit('rowSelect', row.data)
		emit('update:itemsSelected', selections.value)
	}

	async function selectAll() {
		await nextTick()

		emit('selectAll', selections.value)
		emit('update:itemsSelected', selections.value)
	}

	/**
	 * Computed style values based on header width prop if set
	 * @param width
	 */
	function columnStyle(width: number) {
		return {
			width: width ? width + 'px !important' : 'auto',
			'min-width': width ? width + 'px !important' : '0',
			'max-width': width ? width + 'px !important' : undefined
		}
	}

	/**
	 * Computed style values based on header width prop if set
	 * alternatively calculates from item table header widths
	 * @param index
	 * @param width
	 */
	function kitColumnStyle(index: number, width: number) {
		return {
			width: width
				? width + 'px !important'
				: colWidths.value[index]
					? colWidths.value[index] + 'px !important'
					: 'auto',
			'min-width': width
				? width + 'px !important'
				: colWidths.value[index]
					? colWidths.value[index] + 'px !important'
					: '0',
			'max-width': width
				? width + 'px !important'
				: colWidths.value[index]
					? colWidths.value[index] + 'px !important'
					: 'unset'
		}
	}

	/**
	 * Handle kit column widths to match parent table
	 */
	function handleKitColumnWidths() {
		// Wait for DOM update
		nextTick(() => {
			const parentTable = itemTable.value.$el.querySelector('table')
			const parentHeaders = parentTable.querySelectorAll('thead th')
			const widths = []

			parentHeaders.forEach((header) => {
				widths.push(header.offsetWidth)
			})

			colWidths.value = widths
		})
	}

	// Handlers
	useSelectedItemsHandler(selectedItems as any, selectedComponents as any)

	watch(
		() => props.itemsSelected,
		(newSelection) => {
			// Deep compare to prevent re-triggering the same selection back and forth
			if (JSON.stringify(newSelection) !== JSON.stringify(selectedItems.value)) {
				selectedItems.value = newSelection
			}
		},
		{ deep: true }
	)

	watch(
		() => props.loading,
		(newVal) => {
			if (!newVal) {
				if (props.resetSelections) {
					selectedItems.value = []
					selectedComponents.value = []
				}
			}
		}
	)

	// Emits
	const emit = defineEmits([
		'rowClick',
		'update:itemsSelected',
		'rowSelect',
		'selectAll',
		'update:serverOptions',
		'update:expandedRows'
	])

	// Functions
	/**
	 * Check if row is selected using dataKey
	 * @param row
	 */
	const isSelected = (row) => {
		return selectedItems.value.some((item) => item[props.dataKey] == row[props.dataKey])
	}

	onMounted(() => {
		if (props.filterOptions) {
			filters.value = props.filterOptions
		}
	})
</script>

<!-- TODO: look at this responsive stack solution for mobile -->
<style>
	@media (max-width: 767px) {
		#dt-responsive-table table {
			width: 100% !important;
		}

		/* Styles for the table header */
		#dt-responsive-table table thead {
			display: none !important; /* Hide the table header on mobile */
		}

		/* Styles for the table rows */
		#dt-responsive-table table tbody {
			display: flex !important;
			flex-direction: column !important;
			align-items: stretch !important;
			min-height: auto !important; /* Adjust the min-height as needed */
		}

		/* Styles for individual table rows (cards) */
		#dt-responsive-table table tbody tr {
			border: 1px solid #ccc !important;
			border-radius: 8px !important;
			margin-bottom: 1rem !important;
			background-color: #fff !important;
			padding: 1rem !important;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
		}

		/* Styles for table cells within rows */
		#dt-responsive-table table tbody td {
			margin: 0.5rem 0 !important;
		}

		/* Hide individual table rows (cards) on mobile */
		#dt-responsive-table table tbody tr {
			display: table-row !important;
		}

		/* Set the display of table cells to be block-level elements */
		#dt-responsive-table table tbody td {
			display: block !important;
			margin: 0.5rem 0 !important;
		}
	}
</style>
